import { assert } from "@ember/debug";

const WHEN_ORDER = ["morn", "cd", "eve"] as const;
type When = (typeof WHEN_ORDER)[number];
const VALID_PERIOD_UNIT = ["h", "d", "wk", "mo", "a"] as const;
type PeriodUnit = (typeof VALID_PERIOD_UNIT)[number];

/**
 * There are 2 ways to construct a Timing with named parameters or with a string
 * @example
 * new Timing({ frequency: 1, period: 1, periodUnit: "d", when: ["morn"] }) // Every morning
 * new Timing({ frequency: 1, period: 1, periodUnit: "d", when: ["eve", "morn"] }) // Every morning and evening
 * new Timing({ frequency: 3, period: 1, periodUnit: "wk" }) // Three times a week.
 * new Timing({ frequency: 1, period: 2, periodUnit: "mo" }) // Once every two month
 * new Timing({ asNeeded: true }) // As needed
 * Timing.fromComputedId("1-1-d-eve-morn") // Every morning and evening
 */
type RegularTimingPayload = {
  frequency: number;
  period: number;
  periodUnit: PeriodUnit;
  when?: When[];
  asNeeded?: never;
  isOther?: never;
};

type AsNeededTimingPayload = {
  asNeeded: true;
  frequency?: never;
  period?: never;
  periodUnit?: never;
  when?: never;
  isOther?: never;
};

type OtherTimingPayload = {
  isOther: true;
  frequency?: never;
  period?: never;
  periodUnit?: never;
  when?: never;
  asNeeded?: never;
};

export type TimingPayload =
  | RegularTimingPayload
  | AsNeededTimingPayload
  | OtherTimingPayload;

/**
 * It creates an id from the attributes, but it is not really relevant.
 * In fact, "3 times a day" is not equal to "matin midi et soir".
 * And once by month is not equal to once every 4 weeks.
 * So take care of the way you represent the timing.
 */
export default class Timing {
  asNeeded?: boolean;
  frequency?: number;
  isOther?: boolean;
  period?: number;
  /**
   * You can find the different unit here : https://www.hl7.org/fhir/valueset-units-of-time.html
   */
  periodUnit?: PeriodUnit;
  /**
   * You can find the different unit here : https://www.hl7.org/fhir/valueset-event-timing.html
   */
  when?: When[];

  /**
   * Compute an id based on the timing attributes.
   */
  get computedId(): string {
    if (this.asNeeded) {
      return "prn";
    }
    if (this.isOther) {
      return "other";
    }
    let strId = `${this.frequency || 0}-${this.period || 0}-${
      this.periodUnit || ""
    }`;
    this.when?.forEach(function (str) {
      strId += "-" + str;
    });
    return strId.toLowerCase();
  }

  get jsonPayload(): TimingPayload {
    if (this.asNeeded) return { asNeeded: this.asNeeded };
    if (this.isOther) return { isOther: this.isOther };

    assert("frequency is required", this.frequency !== undefined);
    assert("period is required", this.period !== undefined);
    assert("periodUnit is required", this.periodUnit !== undefined);

    const payload: RegularTimingPayload = {
      frequency: this.frequency,
      period: this.period,
      periodUnit: this.periodUnit
    };

    if (this.when) {
      payload.when = this.when;
    }

    return payload;
  }

  constructor({
    frequency,
    period,
    periodUnit,
    when,
    asNeeded,
    isOther
  }: TimingPayload) {
    if (asNeeded) {
      this.asNeeded = true;
      return;
    }
    if (isOther) {
      this.isOther = true;
      return;
    }
    this.frequency = frequency;
    this.period = period;
    this.periodUnit = periodUnit;
    // To keep consistency between different timings we have to order the `when` array
    if (when && when.length > 0) {
      this.when = this.sortWhen([...new Set(when)]);
    }
  }

  static fromComputedId(computedId: string): Timing {
    if (computedId == "prn") return new Timing({ asNeeded: true });
    if (computedId == "other") return new Timing({ isOther: true });

    const attributes = computedId.split("-") as [
      string,
      string,
      PeriodUnit,
      ...When[]
    ];

    assert(
      `The computedId '${computedId}' doesn't respect the format.`,
      attributes.length >= 3
    );
    assert(
      `The periodUnit '${attributes[2]}' isn't supported`,
      VALID_PERIOD_UNIT.includes(attributes[2])
    );

    const timing = new Timing({
      frequency: +attributes[0],
      period: +attributes[1],
      periodUnit: attributes[2]
    });
    for (let i = 3; i < attributes.length; i++) {
      timing.when ??= [];
      const when = attributes[i].toLowerCase() as When;
      assert(
        `The When attribute '${when}' isn't supported`,
        WHEN_ORDER.includes(when)
      );
      timing.when?.push(when);
    }
    return timing;
  }

  isEqual(other: Timing | undefined): boolean {
    return this.computedId === other?.computedId;
  }

  sortWhen(when: When[]): When[] {
    return [...when].sort(
      (a, b) => WHEN_ORDER.indexOf(a) - WHEN_ORDER.indexOf(b)
    );
  }
}
